import React from 'react';
import styled from 'styled-components';
import { Chip, CHIP_VARIANTS } from '@toqio/toqio-web-components';
import { PrimaryButton } from '../../../shared/components';
import { MOBILE, tok } from './styles';

/**
 * Result status tile (PRD §5 / O-5): a bordered card with a neutral grey badge
 * (left), a status `Chip` (right), title + description and an optional action
 * button. Matches the live tiles: fixed ~420px width, both tiles equal height
 * (the row stretches them), neutral badge — the colour lives in the chip.
 */
type Tone = 'warning' | 'danger';

const Box = styled.div`
  flex: 0 0 357px;
  max-width: 100%;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  border: 1px solid ${({ theme }) => tok(theme, 'containers_bordered_border', '#DADADA')};
  border-radius: 12px;
  background: ${({ theme }) => tok(theme, 'general_background', '#FFFFFF')};

  @media (max-width: ${MOBILE}) {
    flex-basis: 100%;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ theme }) => tok(theme, 'avatar_icon_background', '#F4F4F4')};
  color: ${({ theme }) => tok(theme, 'general_font_primary', '#1E1E1E')};
`;

const Title = styled.div`
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  color: ${({ theme }) => tok(theme, 'general_font_primary', '#1E1E1E')};
`;

const Desc = styled.div`
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: ${({ theme }) => tok(theme, 'general_font_tertiary', '#696969')};
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

export type StatusBoxProps = {
  tone: Tone;
  iconNode: React.ReactNode;
  chipLabel: string;
  title: React.ReactNode;
  description: React.ReactNode;
  action?: { label: React.ReactNode; onClick: () => void };
  testId?: string;
};

export const StatusBox: React.FC<StatusBoxProps> = ({
  tone,
  iconNode,
  chipLabel,
  title,
  description,
  action,
  testId,
}) => (
  <Box data-testid={testId}>
    <TopRow>
      <Badge>{iconNode}</Badge>
      <Chip size="s" variant={tone === 'danger' ? CHIP_VARIANTS.DANGER : CHIP_VARIANTS.WARNING}>
        {chipLabel}
      </Chip>
    </TopRow>
    <Body>
      <Title>{title}</Title>
      <Desc>{description}</Desc>
    </Body>
    {action && (
      <PrimaryButton onClick={action.onClick}>{action.label}</PrimaryButton>
    )}
  </Box>
);
